/* eslint-disable react/prop-types */
import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core'
import OpalGrid, { useGridPageState, useGridSortState } from '../../../../organisms/DenaliAnalysis/tables/operational'
import Icon from '../../../../atoms/Icon'
import { useListMigrationBatches } from '../../../../../api/documentVault'
import { useFormattingContext } from '../../../../organisms/FormattingProvider/FormattingContext'
import { getBatchStatus, statusColorStyles } from './migrationUtils'

const useStyles = makeStyles(() => ({
  wrapper: {
    '& .__status': {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '2px 8px',
      borderRadius: 'var(--border-radius-md)',
      fontSize: '0.8rem',
      fontWeight: 600,
      ...statusColorStyles
    },
    '& .og-tr': { cursor: 'pointer' }
  }
}))

const MigrationBatchList = ({ onSelectBatch }) => {
  const classes = useStyles()
  const { formatter } = useFormattingContext()
  const { pageState, setPageState } = useGridPageState(25)
  const { sortState, setSortState } = useGridSortState([{ id: 'batchId', desc: true }])

  const sortField = sortState?.[0]?.id ?? 'batchId'
  const sortDir = sortState?.[0]?.desc ? 'desc' : 'asc'
  const { data, isFetching } = useListMigrationBatches({
    take: pageState.pageSize,
    skip: pageState.pageIndex * pageState.pageSize,
    sortField,
    sortDir
  })

  const batches = useMemo(() => data?.batches ?? [], [data])

  const columns = useMemo(() => [
    {
      accessorKey: 'batchId',
      header: 'ID',
      size: 60
    },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (row) => row.isProcessing,
      cell: ({ row }) => {
        const status = getBatchStatus(row.original)
        return (
          <span className={clsx('__status', status.className)}>
            <Icon name={status.icon} color={status.iconColor} customSize={14} />
            {status.label}
          </span>
        )
      },
      size: 160
    },
    {
      id: 'orionFirmId',
      header: 'Orion Firm',
      accessorFn: (row) => row.metadata?.orionFirmId ?? '-',
      size: 100
    },
    {
      id: 'progress',
      header: 'Progress',
      accessorFn: (row) => row.taskSummary?.completed ?? 0,
      cell: ({ row }) => {
        const s = row.original.taskSummary
        if (!s) return '-'
        return (
          <span>
            {s.completed}/{s.total}
            {s.errored > 0 && <span style={{ color: 'var(--color-danger-500)', marginLeft: '4px' }}>({s.errored} errors)</span>}
          </span>
        )
      },
      size: 150
    },
    {
      accessorKey: 'lastStartedDate',
      header: 'Started',
      cell: ({ getValue }) => getValue() ? formatter(getValue(), '&M/D/YY h:mm a') : '-',
      size: 150
    },
    {
      accessorKey: 'lastFinishedDate',
      header: 'Completed',
      cell: ({ getValue }) => getValue() ? formatter(getValue(), '&M/D/YY h:mm a') : '-',
      size: 150
    }
  ], [formatter])

  const handleTableClick = useCallback((e) => {
    const row = e.target.closest('.og-tr')
    if (!row || !row.parentElement?.classList?.contains('og-tbody')) return
    const rowIndex = Array.from(row.parentElement.children).indexOf(row)
    if (rowIndex >= 0 && batches[rowIndex]) {
      onSelectBatch(batches[rowIndex].batchId)
    }
  }, [batches, onSelectBatch])

  return (
    <div className={classes.wrapper}>
      <div className='font-weight-bold' style={{ marginBottom: '8px' }}>Migration Batches</div>
      <div onClick={handleTableClick}>
        <OpalGrid
          data={batches}
          rowCount={data?.total ?? batches.length}
          columns={columns}
          isFetching={isFetching}
          pageState={pageState}
          sortState={sortState}
          onPageChange={setPageState}
          onSortChange={setSortState}
        />
      </div>
    </div>
  )
}

MigrationBatchList.propTypes = {
  onSelectBatch: PropTypes.func.isRequired
}

export default MigrationBatchList
